import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Input, Popconfirm, Table, Tooltip, message } from "antd";
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  ClearOutlined,
  ItalicOutlined,
  NotificationOutlined,
  OrderedListOutlined,
  SendOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import apiClient from "../../actions/api";
import "../../style/user/news.scss";

const News = () => {
  const editorRef = useRef(null);
  const [title, setTitle] = useState("");
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/news", {
        params: { page, limit: 10 },
      });
      setHistory(data.items || []);
      setTotal(data.pagination?.total || 0);
    } catch (error) {
      message.error(
        error.response?.data?.errors?.[0]?.msg ||
          "Unable to load news history.",
      );
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const runCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    setDescriptionHtml(editorRef.current?.innerHTML || "");
  };

  const publish = async () => {
    const plainDescription = editorRef.current?.innerText?.trim() || "";
    if (title.trim().length < 3) {
      message.warning("Please enter a title with at least 3 characters.");
      return;
    }
    if (!plainDescription) {
      message.warning("Please enter a news description.");
      return;
    }

    setPublishing(true);
    try {
      const { data } = await apiClient.post("/news", {
        title: title.trim(),
        descriptionHtml: editorRef.current?.innerHTML || descriptionHtml,
      });
      message.success(`News published to ${data.recipientCount || 0} users.`);
      setTitle("");
      setDescriptionHtml("");
      if (editorRef.current) editorRef.current.innerHTML = "";
      if (page === 1) await loadHistory();
      else setPage(1);
    } catch (error) {
      message.error(
        error.response?.data?.errors?.[0]?.msg || "Unable to publish news.",
      );
    } finally {
      setPublishing(false);
    }
  };

  const toolbar = [
    ["Bold", <BoldOutlined />, "bold"],
    ["Italic", <ItalicOutlined />, "italic"],
    ["Underline", <UnderlineOutlined />, "underline"],
    ["Strikethrough", <StrikethroughOutlined />, "strikeThrough"],
    ["Bulleted list", <UnorderedListOutlined />, "insertUnorderedList"],
    ["Numbered list", <OrderedListOutlined />, "insertOrderedList"],
    ["Align left", <AlignLeftOutlined />, "justifyLeft"],
    ["Align center", <AlignCenterOutlined />, "justifyCenter"],
    ["Align right", <AlignRightOutlined />, "justifyRight"],
    ["Clear formatting", <ClearOutlined />, "removeFormat"],
  ];

  const columns = [
    {
      title: "News",
      key: "news",
      render: (_, record) => (
        <div className="news-history-copy">
          <strong>{record.title}</strong>
          <p>{record.descriptionText}</p>
        </div>
      ),
    },
    {
      title: "Published by",
      key: "author",
      width: 180,
      render: (_, record) => (
        <div className="news-history-author">
          <strong>{record.createdBy?.name || "Admin"}</strong>
          <span>{record.createdBy?.email || ""}</span>
        </div>
      ),
    },
    {
      title: "Recipients",
      dataIndex: "recipientCount",
      width: 110,
      align: "center",
    },
    {
      title: "Published",
      dataIndex: "createdAt",
      width: 170,
      render: (value) => dayjs(value).format("MMM D, YYYY · h:mm A"),
    },
  ];

  return (
    <section className="news-page">
      <div className="news-heading">
        <div>
          <span className="eyebrow">Administration</span>
          <h1>News & announcements</h1>
          <p>
            Publish product news and important updates to every active user.
          </p>
        </div>
      </div>

      <Card className="news-composer" variant="borderless">
        <div className="news-composer-header">
          <div className="news-composer-icon">
            <NotificationOutlined />
          </div>
          <div>
            <h2>Create announcement</h2>
            <p>Recipients will receive this in their notification center.</p>
          </div>
        </div>

        <label htmlFor="news-title">Title</label>
        <Input
          id="news-title"
          size="large"
          value={title}
          maxLength={160}
          showCount
          placeholder="What would you like everyone to know?"
          onChange={(event) => setTitle(event.target.value)}
        />

        <label className="news-description-label">Description</label>
        <div className="news-rich-editor">
          <div className="news-editor-toolbar">
            <select
              aria-label="Text style"
              defaultValue="p"
              onChange={(event) =>
                runCommand("formatBlock", event.target.value)
              }
            >
              <option value="p">Paragraph</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
              <option value="blockquote">Quote</option>
            </select>
            {toolbar.map(([label, icon, command]) => (
              <Tooltip title={label} key={command}>
                <button
                  type="button"
                  aria-label={label}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => runCommand(command)}
                >
                  {icon}
                </button>
              </Tooltip>
            ))}
          </div>
          <div
            ref={editorRef}
            className="news-editor-canvas"
            contentEditable={!publishing}
            suppressContentEditableWarning
            data-placeholder="Write the announcement details here..."
            onInput={(event) =>
              setDescriptionHtml(event.currentTarget.innerHTML)
            }
          />
        </div>

        <div className="news-publish-row">
          <span>Publishing sends a notification to every active account.</span>
          <Popconfirm
            title="Publish this announcement?"
            description="All active users will receive it immediately."
            okText="Publish"
            onConfirm={publish}
          >
            <Button type="primary" icon={<SendOutlined />} loading={publishing}>
              Publish news
            </Button>
          </Popconfirm>
        </div>
      </Card>

      <Card className="news-history-card" variant="borderless">
        <div className="news-history-heading">
          <div>
            <h2>Notification history</h2>
            <p>A record of announcements sent to users.</p>
          </div>
          <span>{total} published</span>
        </div>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={history}
          loading={loading}
          pagination={{
            current: page,
            pageSize: 10,
            total,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      </Card>
    </section>
  );
};

export default News;
